// ─── Estado automático de etapas para el Dashboard ───────────────────────────
//
// El estado de cada etapa se DERIVA de los datos del proyecto (descriptivo), no
// de una confirmación manual (prescriptivo). Así refleja siempre la realidad
// aunque el usuario retoque una etapa mil veces, sin puertas que bloqueen.
//
// 3 niveles: 'empty' (sin empezar) · 'in_progress' (datos parciales) · 'has_data'
// (el artefacto principal existe). Encima va, opcional, un flag MANUAL "revisado"
// (tabla stage_status, status='completed') que el usuario activa/desactiva libre.
//
// Este módulo es PURO (sin React ni Supabase) → testeable con
// `npx tsx scripts/check-stage-progress.ts`.

export type StageProgress = "empty" | "in_progress" | "has_data";

// Lista de etapas del dashboard. Independiente del tipo estricto StageName (que
// gobierna el sidebar y las dependencias) para poder incluir Festivales y
// Convocatorias sin tocar ese sistema. La clave `key` se usa también como `stage`
// en la tabla stage_status para el flag manual de "revisado".
export interface DashboardStage {
  key: string;
  label: string;
  expert: string;
  route: string;
}

export const DASHBOARD_STAGES: DashboardStage[] = [
  { key: "narrativa", label: "Narrativa", expert: "Script Doctor", route: "/narrativa" },
  { key: "moodboard", label: "Moodboard", expert: "Director de Arte", route: "/moodboard" },
  { key: "localizaciones", label: "Localizaciones", expert: "Jefe/a de Localizaciones", route: "/produccion/localizaciones" },
  { key: "rodaje", label: "Plan de Rodaje", expert: "Ayudante de Dirección", route: "/produccion/rodaje" },
  { key: "casting", label: "Casting", expert: "Director de Casting", route: "/produccion/casting" },
  { key: "diseno_produccion", label: "Diseño de Producción", expert: "Diseñador/a de Producción", route: "/produccion/desglose" },
  { key: "presupuesto", label: "Presupuesto", expert: "Line Producer", route: "/presupuesto/icaa" },
  { key: "financiacion", label: "Financiación", expert: "Productor Ejecutivo", route: "/produccion/financiacion" },
  { key: "audiencias", label: "Diseño de Audiencias", expert: "Agente de Ventas", route: "/audiencias" },
  { key: "festivales", label: "Festivales y Distribución", expert: "Distribuidor/a", route: "/distribucion/festivales" },
  // 'convocatorias' RETIRADA del pipeline: la página mostraba ayudas SIMULADAS y caducadas
  // ("ICAA deadline 15 Mayo 2026 · abierta") como una etapa real. Reconectar a datos reales
  // (tabla `convocatorias`, hoy solo 2025) antes de reponerla.
];

// Señales de datos del proyecto (conteos por tabla + content_types presentes en
// project_content). El Dashboard las obtiene y aquí se traducen a estado.
export interface StageSignals {
  scenes: number;
  narrative: number;
  moodboard: number;
  locations: number;
  casting: number;
  budgetLines: number;
  productionPoints: number;
  distribution: number;
  /** content_type presentes en project_content para este job. */
  contentTypes: Set<string>;
}

export function emptyStageSignals(): StageSignals {
  return {
    scenes: 0, narrative: 0, moodboard: 0, locations: 0, casting: 0,
    budgetLines: 0, productionPoints: 0, distribution: 0, contentTypes: new Set<string>(),
  };
}

export function deriveStageProgress(stageKey: string, s: StageSignals): StageProgress {
  const has = (t: string) => s.contentTypes.has(t);
  switch (stageKey) {
    case "narrativa":
      if (s.narrative > 0) return "has_data";
      if (s.scenes > 0) return "in_progress";
      return "empty";
    case "moodboard":
      return (s.moodboard > 0 || has("moodboard_images")) ? "has_data" : "empty";
    case "localizaciones":
      return s.locations > 0 ? "has_data" : "empty";
    case "rodaje":
      // El plan se calcula de las escenas; con localizaciones asignadas es sólido.
      if (s.scenes > 0 && s.locations > 0) return "has_data";
      if (s.scenes > 0) return "in_progress";
      return "empty";
    case "casting":
      return s.casting > 0 ? "has_data" : "empty";
    case "diseno_produccion":
      // El diseño de producción es la CONSOLIDACIÓN del desglose en unidades.
      if (has("production_design_report") || has("consolidated_inventory") || s.productionPoints > 0) return "has_data";
      // Hay desglose crudo (escenas analizadas) pero aún sin consolidar.
      if (s.scenes > 0) return "in_progress";
      return "empty";
    case "presupuesto":
      return s.budgetLines > 0 ? "has_data" : "empty";
    case "financiacion":
      if (has("financing_plan")) return "has_data";
      if (has("financing_params")) return "in_progress";
      return "empty";
    case "audiencias":
      if (has("audience_dossier") || has("audience_launch_plan")) return "has_data";
      if (has("audience_personas") || has("audience_comps_data")) return "in_progress";
      return "empty";
    case "festivales":
      return (s.distribution > 0 || has("festival_strategy")) ? "has_data" : "empty";
    default:
      return "empty";
  }
}

export const STAGE_PROGRESS_META: Record<StageProgress, { label: string; tone: string }> = {
  empty: { label: "Sin empezar", tone: "faint" },
  in_progress: { label: "En progreso", tone: "amber" },
  has_data: { label: "Con datos", tone: "emerald" },
};
